import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';
import useCountUp from '../shared/useCountUp';
import { useNotification } from '../../contexts/NotificationContext';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';

const P = {
  blue: '#0A0A0A',
  green: '#16A34A',
  orange: '#D97706',
  red: '#DC2626',
  gray: '#6B6B6B',
  dark: '#0A0A0A',
  border: '#E5E5E5',
  bg: '#f5f5f7',
};

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";

// Filter bar (search + selects) — every style here is applied via the
// inline `style` prop, on purpose, never a CSS class. This is the
// deliberately bulletproof fix for a text/icon overlap bug that kept
// recurring through class-based CSS: inline styles are the highest
// specificity there is short of !important, so they can never lose to a
// stray global rule, a stale injected <style> tag left over from a hot
// reload, or a load-order fluke — whatever the exact mechanism was.
// Fixed pixel dimensions on every glyph below (never a Tailwind
// className) for the same reason: nothing here depends on any utility
// class actually resolving.
const FILTER_WRAP_STYLE = {
  flex: 1, minWidth: 200, height: 36, boxSizing: 'border-box',
  display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px',
  border: `1px solid ${P.border}`, borderRadius: 10, background: P.bg,
};
const FILTER_WRAP_FOCUS_STYLE = { borderColor: P.blue, boxShadow: '0 0 0 3px rgba(10,10,10,0.08)', background: '#fff' };
const FILTER_ICON_STYLE = { display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: P.gray, pointerEvents: 'none' };
const FILTER_SEARCH_INPUT_STYLE = {
  flex: 1, minWidth: 0, height: '100%', border: 'none', outline: 'none',
  background: 'transparent', fontSize: 13, color: P.dark, fontFamily: 'inherit', padding: 0,
};
const FILTER_SELECT_WRAP_STYLE = { position: 'relative', display: 'inline-flex', alignItems: 'center' };
const FILTER_SELECT_STYLE = {
  WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
  height: 36, boxSizing: 'border-box', padding: '0 32px 0 12px',
  border: `1px solid ${P.border}`, borderRadius: 10, fontSize: 13, color: P.dark,
  background: P.bg, fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
};
const FILTER_SELECT_FOCUS_STYLE = { borderColor: P.blue, boxShadow: '0 0 0 3px rgba(10,10,10,0.08)' };
const FILTER_CHEVRON_STYLE = {
  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  color: P.gray, pointerEvents: 'none',
};

const FilterSearchGlyph = () => (
  <svg width="15" height="15" style={{ display: 'block', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const FilterChevronGlyph = () => (
  <svg width="13" height="13" style={{ display: 'block', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const STATUS_LABELS = {
  new: 'Nuevo',
  pending_contact: 'Pendiente de contacto',
  contacted: 'Contactado',
  qualified: 'Calificado',
  meeting_scheduled: 'Reunión agendada',
  proposal_sent: 'Propuesta enviada',
  won: 'Ganado',
  lost: 'Perdido',
};

const STATUS_STYLE = {
  new: { bg: 'rgba(22,163,74,0.12)', color: P.green },
  pending_contact: { bg: 'rgba(217,119,6,0.14)', color: P.orange },
  contacted: { bg: 'rgba(10,10,10,0.08)', color: P.dark },
  qualified: { bg: 'rgba(10,10,10,0.08)', color: P.dark },
  meeting_scheduled: { bg: 'rgba(217,119,6,0.14)', color: P.orange },
  proposal_sent: { bg: 'rgba(10,10,10,0.08)', color: P.dark },
  won: { bg: 'rgba(22,163,74,0.16)', color: P.green },
  lost: { bg: 'rgba(220,38,38,0.10)', color: P.red },
};

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nuevo' },
  { value: 'pending_contact', label: 'Pendiente de contacto' },
  { value: 'contacted', label: 'Contactado' },
  { value: 'qualified', label: 'Calificado' },
  { value: 'meeting_scheduled', label: 'Reunión agendada' },
  { value: 'proposal_sent', label: 'Propuesta enviada' },
  { value: 'won', label: 'Ganado' },
  { value: 'lost', label: 'Perdido' },
];

const INTEREST_LABELS = { low: 'Bajo', medium: 'Medio', high: 'Alto' };
const INTEREST_STYLE = {
  low: { bg: 'rgba(0,0,0,0.05)', color: P.gray },
  medium: { bg: 'rgba(217,119,6,0.14)', color: P.orange },
  high: { bg: 'rgba(22,163,74,0.14)', color: P.green },
};
const INTEREST_OPTIONS = [
  { value: 'low', label: 'Bajo' },
  { value: 'medium', label: 'Medio' },
  { value: 'high', label: 'Alto' },
];

const SOURCE_LABELS = {
  public_form: 'Formulario público',
  manual: 'Manual',
  scan: 'Escaneado',
  import: 'Importado',
};

const OUTCOME_LABELS = { pending: 'Pendiente', won: 'Ganado', lost: 'Perdido' };
const OUTCOME_OPTIONS = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'won', label: 'Ganado' },
  { value: 'lost', label: 'Perdido' },
];

// contact.status / .interest_level / .source / .outcome are English-looking
// enum values already (never translate the value, only the label shown).
const statusLabel = (status, t) => t(`hub.status.${status}`, STATUS_LABELS[status] || status);
const interestLabel = (level, t) => t(`hub.interest.${level}`, INTEREST_LABELS[level] || level);
const sourceLabel = (source, t) => t(`hub.source.${source}`, SOURCE_LABELS[source] || source);
const outcomeLabel = (outcome, t) => t(`hub.outcome.${outcome}`, OUTCOME_LABELS[outcome] || outcome);

const legacySourceLabel = (source, t) => {
  if (source === 'lead') return t('hub.source.fromLead', 'Desde Lead');
  return SOURCE_LABELS[source] ? sourceLabel(source, t) : t('hub.source.manual', 'Manual');
};

const formatDateShort = (iso, locale = 'es-CL') =>
  iso ? new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const formatDateTimeShort = (iso, locale = 'es-CL') =>
  iso ? new Date(iso).toLocaleString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const endOfToday = () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; };
const isOverdue = (iso) => !!iso && new Date(iso) < startOfToday();
const isDueToday = (iso) => !!iso && new Date(iso) >= startOfToday() && new Date(iso) <= endOfToday();
const isActionPending = (iso) => !!iso && new Date(iso) <= endOfToday();

const toDateInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toDatetimeLocalValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const normalizeWhatsApp = (phone) => (phone || '').replace(/\D/g, '');

const EMPTY_CREATE_FORM = { name: '', company: '', position: '', email: '', phone: '', notes: '', tags: [], campaign_id: '' };

const nextActionMeta = (contact, locale = 'es-CL') => {
  if (!contact.next_action_description && !contact.next_action_due_at) {
    return { empty: true };
  }
  return {
    empty: false,
    description: contact.next_action_description || '',
    dateLabel: contact.next_action_due_at ? formatDateShort(contact.next_action_due_at, locale) : '',
    overdue: isOverdue(contact.next_action_due_at),
    dueToday: isDueToday(contact.next_action_due_at),
  };
};

const errorMessageForStatus = (status, t) => {
  if (status === 401) return t('hub.error.sessionExpired', 'Tu sesión expiró. Recarga la página e inicia sesión nuevamente.');
  if (status === 403) return t('hub.error.noAccess', 'No tienes acceso a esta sección.');
  return t('hub.error.loadFailed', 'No se pudieron cargar los contactos. Intenta nuevamente.');
};

const CloseIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronIcon = ({ className = 'w-4 h-4', open }) => (
  <svg className={className} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChatIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const CSS = `
  .hb-wrap{font-family:${FONT_UI};color:${P.dark};}
  .hb-header-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;}
  .hb-title{font-family:${FONT};font-size:30px;font-weight:300;letter-spacing:-0.01em;color:${P.dark};margin:0;}
  .hb-subtitle{font-size:14px;color:${P.gray};margin:4px 0 0;font-weight:400;}
  .hb-header-actions{display:flex;gap:10px;flex-wrap:wrap;}
  .hb-btn{font-family:${FONT_UI};font-size:13.5px;font-weight:600;border-radius:9px;cursor:pointer;letter-spacing:-0.01em;transition:background .15s,border-color .15s,opacity .15s;display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:9px 16px;border:1px solid transparent;}
  .hb-btn:disabled{opacity:.55;cursor:not-allowed;}
  .hb-btn-primary{background:${P.blue};color:#fff;}
  .hb-btn-primary:hover:not(:disabled){background:#1a1a1a;}
  .hb-btn-secondary{background:#fff;color:${P.dark};border-color:${P.border};}
  .hb-btn-secondary:hover:not(:disabled){background:#FAFAFA;border-color:#D4D4D4;}
  .hb-btn-text{background:none;border:none;color:${P.gray};font-family:${FONT_UI};font-size:13px;font-weight:500;cursor:pointer;padding:4px 6px;}
  .hb-btn-text:hover{color:${P.dark};}
  .hb-btn-danger-text{background:none;border:none;color:${P.red};font-family:${FONT_UI};font-size:13.5px;font-weight:600;cursor:pointer;padding:9px 12px;}
  .hb-btn-danger-text:hover:not(:disabled){opacity:.75;}

  .hb-metrics{display:flex;background:#fff;border-radius:14px;border:1px solid ${P.border};overflow:hidden;margin-top:18px;}
  .hb-metric-card{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;padding:14px 10px;border-right:1px solid ${P.border};}
  .hb-metric-card:last-child{border-right:none;}
  .hb-metric-value{font-size:20px;font-weight:700;letter-spacing:-0.3px;color:${P.dark};line-height:1;font-variant-numeric:tabular-nums;}
  .hb-metric-label{font-size:11.5px;color:${P.gray};font-weight:500;margin-top:2px;text-align:center;}
  .hb-metrics-caption{font-size:11px;color:${P.gray};margin:6px 2px 0;}

  .hb-filters{background:#fff;border-radius:14px;border:1px solid ${P.border};padding:14px 16px;display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-top:14px;}
  /* The search box and the 3 filter selects are styled entirely via
     inline style props (see FILTER_*_STYLE constants above) on
     purpose, not class names here, so there is no CSS specificity or
     load-order for a stray rule to ever win against. */
  .hb-toggle{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;color:${P.gray};font-weight:500;cursor:pointer;user-select:none;}
  .hb-toggle input{cursor:pointer;}

  .hb-error-banner{margin-top:14px;border-radius:10px;padding:14px 16px;font-size:13.5px;border:1px solid rgba(220,38,38,0.25);background:rgba(220,38,38,0.06);color:${P.red};display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;}

  .hb-table-wrap{margin-top:16px;background:#fff;border-radius:14px;border:1px solid ${P.border};overflow:hidden;}
  .hb-table{width:100%;border-collapse:collapse;}
  .hb-table th{text-align:left;font-size:11.5px;font-weight:600;color:${P.gray};text-transform:uppercase;letter-spacing:.4px;padding:12px 16px;border-bottom:1px solid ${P.border};background:${P.bg};}
  .hb-table td{padding:14px 16px;border-bottom:1px solid ${P.border};font-size:13.5px;vertical-align:top;}
  .hb-table tr:last-child td{border-bottom:none;}
  .hb-table-row{cursor:pointer;transition:background .12s;}
  .hb-table-row:hover{background:#FAFAFA;}
  .hb-table-row:focus-visible{outline:2px solid ${P.blue};outline-offset:-2px;}
  .hb-cell-name{font-weight:600;color:${P.dark};}
  .hb-cell-sub{font-size:12px;color:${P.gray};margin-top:2px;}
  .hb-cell-info-row{display:flex;align-items:center;gap:5px;font-size:12.5px;color:${P.gray};margin-top:3px;}
  .hb-cell-info-row:first-child{margin-top:0;}
  @media(max-width:900px){.hb-table-wrap{display:none;}}

  .hb-badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11.5px;font-weight:600;letter-spacing:.1px;white-space:nowrap;}
  .hb-source-badge{display:inline-flex;align-items:center;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:500;background:rgba(0,0,0,0.05);color:${P.gray};}
  .hb-action-empty{color:${P.gray};font-size:12.5px;}
  .hb-action-desc{color:${P.dark};font-size:12.5px;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .hb-action-date{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:600;padding:2px 8px;border-radius:20px;margin-top:4px;}
  .hb-action-date-overdue{background:rgba(220,38,38,0.12);color:${P.red};}
  .hb-action-date-today{background:rgba(217,119,6,0.14);color:${P.orange};}
  .hb-action-date-future{background:rgba(0,0,0,0.05);color:${P.gray};}

  .hb-cards-wrap{display:none;margin-top:16px;flex-direction:column;gap:12px;}
  @media(max-width:900px){.hb-cards-wrap{display:flex;}}
  .hb-mobile-card{background:#fff;border:1px solid ${P.border};border-radius:14px;padding:16px;cursor:pointer;transition:box-shadow .15s;}
  .hb-mobile-card:active{box-shadow:0 4px 16px rgba(0,0,0,0.08);}
  .hb-mobile-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;}
  .hb-mobile-name{font-size:15.5px;font-weight:600;color:${P.dark};}
  .hb-mobile-sub{font-size:12.5px;color:${P.gray};margin-top:2px;}
  .hb-mobile-badges{display:flex;gap:6px;flex-wrap:wrap;margin-top:10px;}
  .hb-mobile-info{margin-top:10px;display:flex;flex-direction:column;gap:5px;}
  .hb-mobile-action{margin-top:10px;padding-top:10px;border-top:1px solid ${P.border};}
  .hb-mobile-actions{display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;}
  .hb-mobile-action-btn{flex:1;min-width:90px;height:34px;border-radius:10px;font-size:12.5px;font-weight:500;display:inline-flex;align-items:center;justify-content:center;gap:5px;border:1px solid ${P.border};background:#fff;color:${P.dark};text-decoration:none;cursor:pointer;font-family:inherit;}
  .hb-mobile-action-btn:hover{background:#FAFAFA;}

  .hb-empty{background:#fff;border-radius:14px;border:1px solid ${P.border};padding:56px 24px;text-align:center;margin-top:16px;}
  .hb-empty-title{font-size:17px;font-weight:600;color:${P.dark};margin-bottom:6px;}
  .hb-empty-text{font-size:13.5px;color:${P.gray};margin-bottom:18px;}

  .hb-drawer-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.35);z-index:60;display:flex;justify-content:flex-end;}
  .hb-drawer{background:#fff;width:100%;max-width:460px;height:100%;overflow-y:auto;box-shadow:-12px 0 40px rgba(0,0,0,0.18);}
  @media(max-width:640px){.hb-drawer{max-width:100%;}}
  .hb-drawer-header{position:sticky;top:0;background:#fff;padding:20px 22px 16px;border-bottom:1px solid ${P.border};z-index:2;}
  .hb-drawer-header-top{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;}
  .hb-drawer-name{font-size:19px;font-weight:700;color:${P.dark};letter-spacing:-0.2px;margin:0;}
  .hb-drawer-sub{font-size:13px;color:${P.gray};margin-top:3px;}
  .hb-drawer-meta{font-size:11.5px;color:${P.gray};margin-top:8px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
  .hb-drawer-close{width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,0.06);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:${P.gray};transition:background .15s;flex-shrink:0;}
  .hb-drawer-close:hover{background:rgba(0,0,0,0.10);}
  .hb-drawer-body{padding:20px 22px 28px;}
  .hb-drawer-section{margin-bottom:22px;}
  .hb-drawer-section-title{font-size:11px;font-weight:700;color:${P.gray};text-transform:uppercase;letter-spacing:.6px;margin-bottom:10px;}
  .hb-drawer-caption{font-size:11.5px;color:${P.gray};margin-top:6px;}
  .hb-field{margin-bottom:12px;}
  .hb-field:last-child{margin-bottom:0;}
  .hb-field-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .hb-label{display:block;font-size:12px;font-weight:500;color:#404040;margin-bottom:5px;}
  .hb-field-input,.hb-field-select,.hb-field-textarea{width:100%;border:1px solid ${P.border};border-radius:10px;padding:9px 12px;font-size:13.5px;color:${P.dark};font-family:inherit;outline:none;box-sizing:border-box;background:#fff;transition:border-color .15s,box-shadow .15s;}
  .hb-field-input:focus,.hb-field-select:focus,.hb-field-textarea:focus{border-color:${P.blue};box-shadow:0 0 0 3px rgba(10,10,10,0.08);}
  .hb-field-textarea{resize:vertical;min-height:70px;}
  .hb-field-select{cursor:pointer;}
  .hb-contact-row{display:flex;align-items:center;gap:8px;margin-bottom:10px;}
  .hb-contact-row:last-child{margin-bottom:0;}
  .hb-contact-row-text{flex:1;font-size:13.5px;color:${P.dark};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .hb-quick-btn{height:32px;padding:0 12px;border-radius:9px;border:1px solid ${P.border};background:#fff;color:${P.dark};font-size:12.5px;font-weight:500;display:inline-flex;align-items:center;gap:5px;cursor:pointer;text-decoration:none;font-family:inherit;transition:background .15s;flex-shrink:0;}
  .hb-quick-btn:hover{background:#FAFAFA;}
  .hb-tag{display:inline-flex;align-items:center;gap:6px;background:rgba(10,10,10,0.06);color:${P.dark};padding:4px 11px;border-radius:999px;font-size:12.5px;}
  .hb-tag-remove{background:none;border:none;cursor:pointer;color:${P.gray};font-size:14px;line-height:1;padding:0;}
  .hb-tag-remove:hover{color:${P.red};}
  .hb-tag-row{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px;}
  .hb-tag-add-row{display:flex;gap:8px;}
  .hb-tag-add-btn{height:36px;padding:0 14px;background:${P.bg};color:#404040;border:none;border-radius:10px;font-size:13px;cursor:pointer;font-family:inherit;}
  .hb-tag-add-btn:hover{background:#EDEDED;}
  .hb-meta-list{font-size:12px;color:${P.gray};display:flex;flex-direction:column;gap:4px;}
  .hb-drawer-footer{display:flex;gap:10px;padding-top:18px;border-top:1px solid ${P.border};margin-top:4px;flex-wrap:wrap;}
  .hb-btn-save{flex:1;min-width:120px;height:42px;background:${P.blue};color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;transition:opacity .15s;}
  .hb-btn-save:hover:not(:disabled){opacity:.86;}
  .hb-btn-save:disabled{opacity:.5;cursor:not-allowed;}
  .hb-btn-cancel{height:42px;padding:0 18px;background:transparent;border:1px solid ${P.border};border-radius:10px;font-size:14px;font-weight:500;color:${P.dark};cursor:pointer;font-family:inherit;}
  .hb-btn-cancel:hover{background:rgba(0,0,0,0.04);}

  .hb-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:60;display:flex;align-items:center;justify-content:center;padding:16px;}
  .hb-modal{background:#fff;border:1px solid ${P.border};border-radius:16px;box-shadow:0 24px 72px rgba(0,0,0,0.22);max-width:560px;width:100%;max-height:90vh;overflow-y:auto;}
  .hb-modal-inner{padding:24px;}
  .hb-modal-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;}
  .hb-modal-title{font-family:${FONT_UI};font-size:19px;font-weight:700;color:${P.dark};margin:0;}

  .hb-old-section{margin-top:28px;}
  .hb-old-toggle{width:100%;display:flex;align-items:center;justify-content:space-between;background:#fff;border:1px solid ${P.border};border-radius:14px;padding:14px 18px;cursor:pointer;font-family:inherit;}
  .hb-old-toggle-left{display:flex;align-items:center;gap:10px;}
  .hb-old-title{font-size:14px;font-weight:600;color:${P.dark};}
  .hb-old-count{background:rgba(0,0,0,0.06);color:${P.gray};font-size:11.5px;font-weight:600;padding:2px 9px;border-radius:20px;}
  .hb-old-desc{font-size:12px;color:${P.gray};margin:10px 2px 12px;}
  .hb-old-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;}
  @media(max-width:480px){.hb-old-grid{grid-template-columns:1fr;}}
  .hb-old-card{background:#fff;border:1px solid ${P.border};border-radius:12px;padding:14px 16px;}
  .hb-old-card-name{font-size:13.5px;font-weight:600;color:${P.dark};}
  .hb-old-card-sub{font-size:12px;color:${P.gray};margin-top:2px;}
  .hb-old-card-row{font-size:12px;color:${P.gray};margin-top:6px;display:flex;align-items:center;gap:6px;}

  @media(max-width:768px){
    .hb-title{font-size:20px!important;}
    .hb-subtitle{font-size:11px!important;margin-top:2px!important;}
    .hb-header-row{gap:10px!important;}
    .hb-header-actions{gap:6px!important;}
    .hb-btn{font-size:11px!important;padding:7px 10px!important;min-height:34px!important;}
    .hb-btn-text{font-size:10.5px!important;padding:3px 4px!important;}
    .hb-btn-danger-text{font-size:11px!important;padding:7px 8px!important;min-height:34px!important;}

    .hb-metrics{margin-top:11px!important;border-radius:10px!important;}
    .hb-metric-card{padding:9px 6px!important;gap:1px!important;}
    .hb-metric-value{font-size:14px!important;}
    .hb-metric-label{font-size:10.5px!important;margin-top:1px!important;}
    .hb-metrics-caption{font-size:10.5px!important;margin:4px 2px 0!important;}

    .hb-filters{padding:9px 10px!important;gap:6px!important;margin-top:9px!important;border-radius:10px!important;}
    .hb-toggle{font-size:10.5px!important;gap:4px!important;}

    .hb-error-banner{margin-top:9px!important;padding:9px 10px!important;font-size:11px!important;gap:8px!important;}

    .hb-badge{padding:2px 7px!important;font-size:10.5px!important;}
    .hb-source-badge{padding:2px 6px!important;font-size:10.5px!important;}
    .hb-action-empty{font-size:10.5px!important;}
    .hb-action-desc{font-size:10.5px!important;}
    .hb-action-date{font-size:10.5px!important;padding:2px 6px!important;margin-top:2px!important;}

    .hb-cards-wrap{margin-top:10px!important;gap:7px!important;}
    .hb-mobile-card{padding:10px!important;border-radius:10px!important;}
    .hb-mobile-name{font-size:12.5px!important;}
    .hb-mobile-sub{font-size:10.5px!important;margin-top:1px!important;}
    .hb-mobile-badges{gap:4px!important;margin-top:6px!important;}
    .hb-mobile-info{margin-top:6px!important;gap:3px!important;}
    .hb-mobile-action{margin-top:6px!important;padding-top:6px!important;}
    .hb-mobile-actions{gap:5px!important;margin-top:7px!important;}
    .hb-mobile-action-btn{min-width:70px!important;height:34px!important;font-size:10.5px!important;gap:3px!important;border-radius:8px!important;}

    .hb-empty{padding:36px 14px!important;margin-top:10px!important;border-radius:10px!important;}
    .hb-empty-title{font-size:13px!important;margin-bottom:4px!important;}
    .hb-empty-text{font-size:10.5px!important;margin-bottom:11px!important;}

    .hb-drawer-header{padding:12px 13px 10px!important;}
    .hb-drawer-body{padding:12px 13px 16px!important;}
    .hb-drawer-name{font-size:14px!important;}
    .hb-drawer-sub{font-size:10.5px!important;}
    .hb-drawer-meta{font-size:10.5px!important;margin-top:5px!important;gap:4px!important;}
    .hb-drawer-close{width:26px!important;height:26px!important;}
    .hb-drawer-section{margin-bottom:13px!important;}
    .hb-drawer-section-title{font-size:10.5px!important;margin-bottom:6px!important;}
    .hb-drawer-caption{font-size:10.5px!important;margin-top:4px!important;}
    .hb-field{margin-bottom:8px!important;}
    .hb-field-row{gap:6px!important;}
    .hb-label{font-size:10.5px!important;margin-bottom:3px!important;}
    .hb-field-input,.hb-field-select,.hb-field-textarea{padding:7px 8px!important;font-size:11px!important;}
    .hb-contact-row{gap:5px!important;margin-bottom:6px!important;}
    .hb-contact-row-text{font-size:11px!important;}
    .hb-quick-btn{height:34px!important;padding:0 8px!important;font-size:10.5px!important;gap:3px!important;}
    .hb-tag{padding:2px 7px!important;font-size:10.5px!important;gap:4px!important;}
    .hb-tag-row{gap:4px!important;margin-bottom:5px!important;}
    .hb-tag-add-row{gap:5px!important;}
    .hb-tag-add-btn{height:34px!important;padding:0 9px!important;font-size:10.5px!important;}
    .hb-meta-list{font-size:10.5px!important;gap:2px!important;}
    .hb-drawer-footer{gap:6px!important;padding-top:11px!important;margin-top:2px!important;}
    .hb-btn-save{min-width:84px!important;height:34px!important;font-size:11.5px!important;}
    .hb-btn-cancel{height:34px!important;padding:0 11px!important;font-size:11.5px!important;}

    .hb-modal-inner{padding:14px!important;}
    .hb-modal-header{margin-bottom:12px!important;}
    .hb-modal-title{font-size:14px!important;}

    .hb-old-section{margin-top:17px!important;}
    .hb-old-toggle{padding:9px 11px!important;border-radius:10px!important;}
    .hb-old-title{font-size:10.5px!important;}
    .hb-old-count{font-size:10.5px!important;padding:2px 6px!important;}
    .hb-old-desc{font-size:10.5px!important;margin:6px 2px 8px!important;}
    .hb-old-grid{gap:6px!important;}
    .hb-old-card{padding:9px 10px!important;border-radius:9px!important;}
    .hb-old-card-name{font-size:10.5px!important;}
    .hb-old-card-sub{font-size:10.5px!important;}
    .hb-old-card-row{font-size:10.5px!important;margin-top:4px!important;gap:4px!important;}
  }
`;

const HubPanel = ({ user }) => {
  const { toast, confirm } = useNotification();
  const { hasFeature } = usePlan();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [campaigns, setCampaigns] = useState([]);

  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 200, offset: 0 });
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [metricsContacts, setMetricsContacts] = useState([]);
  const [metricsPagination, setMetricsPagination] = useState({ total: 0, limit: 200, offset: 0 });
  const [metricsLoading, setMetricsLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  // Tracks which filter control currently has focus, purely to draw the
  // focus ring inline (see FILTER_*_FOCUS_STYLE below) — inline styles
  // can't express :focus/:focus-within, so this replaces that with plain
  // state instead of depending on a CSS pseudo-class at all.
  const [focusedFilter, setFocusedFilter] = useState(null);

  const [selectedContact, setSelectedContact] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerForm, setDrawerForm] = useState(null);
  const [newDrawerTag, setNewDrawerTag] = useState('');
  const [saving, setSaving] = useState(false);
  const drawerRef = useRef(null);
  const originalDrawerFormRef = useRef(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_CREATE_FORM);
  const [newTag, setNewTag] = useState('');
  const [scanning, setScanning] = useState(false);
  const [creating, setCreating] = useState(false);

  const [oldContacts, setOldContacts] = useState([]);
  const [oldContactsLoaded, setOldContactsLoaded] = useState(false);
  const [oldContactsOpen, setOldContactsOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Loads the account's campaigns so the "Agregar contacto" form can offer
  // an optional campaign picker, smart-defaulted to the one active campaign
  // when there's exactly one — skipped entirely on plans without the
  // feature so a Free/Pro-without-campaigns account never sees a 402.
  useEffect(() => {
    if (!hasFeature('campaigns')) return;
    axios.get(`${API}/campaigns`)
      .then((res) => { if (isMountedRef.current) setCampaigns(res.data?.campaigns || []); })
      .catch(() => {});
  }, [hasFeature]);

  // Smart-default: when the add-contact form opens and there's exactly one
  // active campaign, pre-select it — one less click during an event, still
  // fully editable/clearable before saving.
  useEffect(() => {
    if (!showAddForm || formData.campaign_id) return;
    const active = campaigns.filter((c) => c.status === 'active');
    if (active.length === 1) {
      setFormData((f) => (f.campaign_id ? f : { ...f, campaign_id: active[0].id }));
    }
  }, [showAddForm, campaigns]);

  const loadMetrics = useCallback(async () => {
    try {
      setMetricsLoading(true);
      const res = await axios.get(`${API}/contacts`, { params: { limit: 200, offset: 0 } });
      if (!isMountedRef.current) return;
      setMetricsContacts(res.data?.contacts || []);
      setMetricsPagination(res.data?.pagination || { total: 0, limit: 200, offset: 0 });
    } catch (err) {
      console.error('[Hub] Error loading metrics', err);
    } finally {
      if (isMountedRef.current) setMetricsLoading(false);
    }
  }, []);

  const loadFilteredContacts = useCallback(async () => {
    try {
      setListLoading(true);
      setLoadError(null);
      const params = { limit: 200, offset: 0 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (interestFilter) params.interest_level = interestFilter;
      if (includeArchived) params.include_archived = true;
      const res = await axios.get(`${API}/contacts`, { params });
      if (!isMountedRef.current) return;
      setContacts(res.data?.contacts || []);
      setPagination(res.data?.pagination || { total: 0, limit: 200, offset: 0 });
    } catch (err) {
      if (isMountedRef.current) setLoadError(errorMessageForStatus(err.response?.status, t));
    } finally {
      if (isMountedRef.current) {
        setListLoading(false);
        setInitialLoading(false);
      }
    }
  }, [debouncedSearch, statusFilter, sourceFilter, interestFilter, includeArchived]);

  const loadOldContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hub/contacts`);
      if (!isMountedRef.current) return;
      const list = Array.isArray(res.data) ? res.data : [];
      setOldContacts(list.filter((hc) => !hc.contact_id));
    } catch (err) {
      console.error('[Hub] Error loading legacy hub contacts', err);
    } finally {
      if (isMountedRef.current) setOldContactsLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadMetrics();
    loadOldContacts();
  }, [loadMetrics, loadOldContacts]);

  useEffect(() => {
    loadFilteredContacts();
  }, [loadFilteredContacts]);

  const refreshAfterMutation = async () => {
    await Promise.all([loadFilteredContacts(), loadMetrics()]);
  };

  const metrics = useMemo(() => {
    const total = metricsPagination.total || 0;
    const capped = total > 200;
    const nuevos = metricsContacts.filter((c) => c.status === 'new').length;
    const pendientes = metricsContacts.filter((c) => c.status === 'pending_contact').length;
    const accionesPendientes = metricsContacts.filter((c) => isActionPending(c.next_action_due_at)).length;
    return { total, nuevos, pendientes, accionesPendientes, capped };
  }, [metricsContacts, metricsPagination]);

  const totalAnim = useCountUp(metrics.total);
  const nuevosAnim = useCountUp(metrics.nuevos);
  const pendientesAnim = useCountUp(metrics.pendientes);
  const accionesPendientesAnim = useCountUp(metrics.accionesPendientes);

  const hasActiveFilters = !!(search || statusFilter || sourceFilter || interestFilter || includeArchived);
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setSourceFilter('');
    setInterestFilter('');
    setIncludeArchived(false);
  };

  // --- Create manual / scan ---
  const handleAddTag = () => {
    const t = newTag.trim();
    if (t && !formData.tags.includes(t)) {
      setFormData({ ...formData, tags: [...formData.tags, t] });
    }
    setNewTag('');
  };
  const handleRemoveTag = (tag) => setFormData({ ...formData, tags: formData.tags.filter((t) => t !== tag) });

  const handleScanCard = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setScanning(true);
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/hub/scan-card`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!isMountedRef.current) return;
      setFormData({
        name: res.data?.name || '',
        company: res.data?.company || '',
        position: res.data?.position || '',
        email: res.data?.email || '',
        phone: res.data?.phone || '',
        notes: res.data?.notes || '',
        tags: ['Scan'],
        campaign_id: '',
      });
      setShowAddForm(true);
      toast.success(t('hub.toast.scanned', 'Tarjeta escaneada. Revisa los datos antes de guardar.'));
    } catch (err) {
      if (isMountedRef.current) toast.error(t('hub.toast.scanError', 'No se pudo escanear la tarjeta. Intenta nuevamente.'));
    } finally {
      if (isMountedRef.current) setScanning(false);
      e.target.value = '';
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (!formData.name.trim()) {
      toast.error(t('hub.toast.nameRequired', 'El nombre es obligatorio'));
      return;
    }
    try {
      setCreating(true);
      const res = await axios.post(`${API}/hub/contacts`, { ...formData, campaign_id: formData.campaign_id || null });
      if (!isMountedRef.current) return;
      if (res.data?.success) {
        toast.success(t('hub.toast.contactAdded', 'Contacto agregado'));
        setShowAddForm(false);
        setFormData(EMPTY_CREATE_FORM);
        await refreshAfterMutation();
      } else {
        toast.error(res.data?.error || t('hub.toast.addError', 'No se pudo agregar el contacto'));
      }
    } catch (err) {
      if (isMountedRef.current) toast.error(t('hub.toast.addError', 'No se pudo agregar el contacto'));
    } finally {
      if (isMountedRef.current) setCreating(false);
    }
  };

  // --- Drawer ---
  const buildDrawerForm = (contact) => ({
    full_name: contact.full_name || '',
    company: contact.company || '',
    position: contact.position || '',
    email: contact.email || '',
    phone: contact.phone || '',
    status: contact.status || 'new',
    interest_level: contact.interest_level || '',
    tags: contact.tags || [],
    notes: contact.notes || '',
    next_action_description: contact.next_action_description || '',
    next_action_due_at: toDateInputValue(contact.next_action_due_at),
    meeting_scheduled_at: toDatetimeLocalValue(contact.meeting_scheduled_at),
    outcome: contact.outcome || '',
    outcome_reason: contact.outcome_reason || '',
  });

  const openDrawer = (contact) => {
    const form = buildDrawerForm(contact);
    setSelectedContact(contact);
    setDrawerForm(form);
    originalDrawerFormRef.current = form;
    setNewDrawerTag('');
    setDrawerOpen(true);
  };

  const forceCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedContact(null);
    setDrawerForm(null);
    originalDrawerFormRef.current = null;
  }, []);

  // Closing via overlay click / Escape / the X / "Cancelar" all go through
  // here so an in-progress edit is never discarded silently. Callers that
  // close after their own successful mutation (e.g. handleArchive) use
  // forceCloseDrawer directly since there's nothing left to lose there.
  const closeDrawer = useCallback(() => {
    const isDirty = drawerForm && JSON.stringify(drawerForm) !== JSON.stringify(originalDrawerFormRef.current);
    if (!isDirty) {
      forceCloseDrawer();
      return;
    }
    confirm(t('hub.confirm.unsavedExit', 'Tienes cambios sin guardar en esta ficha. ¿Salir de todas formas?'), { confirmLabel: t('hub.confirm.unsavedExitLabel', 'Salir sin guardar'), danger: true })
      .then((ok) => { if (ok) forceCloseDrawer(); });
  }, [drawerForm, forceCloseDrawer, confirm]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    const t = setTimeout(() => {
      drawerRef.current?.querySelector('input,textarea,select,button')?.focus();
    }, 50);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(t);
    };
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen && !showAddForm) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen, showAddForm]);

  const handleAddDrawerTag = () => {
    const t = newDrawerTag.trim();
    if (!t) return;
    if (drawerForm.tags.includes(t)) {
      setNewDrawerTag('');
      return;
    }
    setDrawerForm({ ...drawerForm, tags: [...drawerForm.tags, t] });
    setNewDrawerTag('');
  };
  const handleRemoveDrawerTag = (tag) => setDrawerForm({ ...drawerForm, tags: drawerForm.tags.filter((t) => t !== tag) });

  // Fields the user can explicitly clear back to null. Compares the
  // current drawer value against the value captured when the drawer was
  // opened (originalDrawerFormRef): unchanged-and-empty is omitted from
  // the payload (leave untouched server-side); changed-to-empty is sent
  // as null (clear it); non-empty is sent as-is. This is what lets the
  // backend's exclude_unset-based PUT tell "not edited" apart from
  // "edited to empty" — see backend/contacts.py::update_contact.
  const applyClearableField = (payload, key, original, current) => {
    const cur = (current ?? '').toString().trim();
    const orig = (original ?? '').toString().trim();
    if (cur) payload[key] = current;
    else if (orig) payload[key] = null;
  };

  const handleSaveContact = async () => {
    if (saving || !selectedContact || !drawerForm) return;
    if (!drawerForm.full_name.trim()) {
      toast.error(t('hub.toast.nameRequired', 'El nombre es obligatorio'));
      return;
    }
    const original = originalDrawerFormRef.current || {};
    try {
      setSaving(true);
      const payload = {
        full_name: drawerForm.full_name.trim(),
        status: drawerForm.status,
        tags: drawerForm.tags,
      };

      applyClearableField(payload, 'company', original.company, drawerForm.company);
      applyClearableField(payload, 'position', original.position, drawerForm.position);
      applyClearableField(payload, 'email', original.email, drawerForm.email);
      applyClearableField(payload, 'phone', original.phone, drawerForm.phone);
      applyClearableField(payload, 'notes', original.notes, drawerForm.notes);
      applyClearableField(payload, 'interest_level', original.interest_level, drawerForm.interest_level);
      applyClearableField(payload, 'next_action_description', original.next_action_description, drawerForm.next_action_description);

      if (drawerForm.next_action_due_at) {
        payload.next_action_due_at = new Date(drawerForm.next_action_due_at).toISOString();
      } else if (original.next_action_due_at) {
        payload.next_action_due_at = null;
      }
      if (drawerForm.meeting_scheduled_at) {
        payload.meeting_scheduled_at = new Date(drawerForm.meeting_scheduled_at).toISOString();
      } else if (original.meeting_scheduled_at) {
        payload.meeting_scheduled_at = null;
      }

      const leavingOutcomeStage = ['won', 'lost'].includes(original.status) && !['won', 'lost'].includes(drawerForm.status);
      if (leavingOutcomeStage) {
        payload.outcome = null;
        payload.outcome_reason = null;
      } else if (['won', 'lost'].includes(drawerForm.status)) {
        applyClearableField(payload, 'outcome', original.outcome, drawerForm.outcome);
        applyClearableField(payload, 'outcome_reason', original.outcome_reason, drawerForm.outcome_reason);
      }

      const res = await axios.put(`${API}/contacts/${selectedContact.id}`, payload);
      if (!isMountedRef.current) return;
      const updated = res.data?.contact;
      if (updated) {
        const nextForm = buildDrawerForm(updated);
        setSelectedContact(updated);
        setDrawerForm(nextForm);
        originalDrawerFormRef.current = nextForm;
      }
      toast.success(t('hub.toast.contactUpdated', 'Contacto actualizado'));

      if (updated?.legacy_hub_contact_id) {
        try {
          await axios.put(`${API}/hub/contacts/${updated.legacy_hub_contact_id}`, {
            name: updated.full_name,
            company: updated.company,
            position: updated.position,
            email: updated.email,
            phone: updated.phone,
            notes: updated.notes,
            tags: updated.tags,
          });
        } catch (syncErr) {
          console.error('[Hub] Secondary sync to legacy HubContact failed', syncErr);
          if (isMountedRef.current) toast.error(t('hub.toast.syncError', 'Se guardó el contacto, pero no se pudo sincronizar con Hub.'));
        }
      }

      await refreshAfterMutation();
    } catch (err) {
      if (isMountedRef.current) toast.error(t('hub.toast.saveError', 'No se pudo guardar el contacto'));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedContact) return;
    // Legacy hub_contacts records are hard-deleted by the backend (only a
    // *linked* new-system Contact gets archived, if one exists) — so the
    // reversible "archivar" framing is only accurate for non-legacy contacts.
    // That's also why only this branch skips the confirm dialog below in
    // favor of an undo toast: it's the only one that's actually reversible.
    const isLegacyOnly = !!selectedContact.legacy_hub_contact_id;

    if (isLegacyOnly) {
      const ok = await confirm(
        t('hub.confirm.deleteLegacy', '¿Eliminar este contacto? Es un registro antiguo y esta acción no se puede deshacer.'),
        { confirmLabel: t('hub.confirm.deleteLegacyLabel', 'Eliminar contacto'), danger: true }
      );
      if (!ok) return;
      try {
        await axios.delete(`${API}/hub/contacts/${selectedContact.legacy_hub_contact_id}`);
        if (!isMountedRef.current) return;
        toast.success(t('hub.toast.deleted', 'Contacto eliminado'));
        forceCloseDrawer();
        await refreshAfterMutation();
      } catch (err) {
        if (isMountedRef.current) toast.error(t('hub.toast.deleteError', 'No se pudo eliminar el contacto'));
      }
      return;
    }

    const contactId = selectedContact.id;
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      if (!isMountedRef.current) return;
      forceCloseDrawer();
      await refreshAfterMutation();
      toast.undo(t('hub.toast.archived', 'Contacto archivado. Actívalo con "Mostrar archivados".'), async () => {
        try {
          await axios.put(`${API}/contacts/${contactId}`, { is_archived: false });
          await refreshAfterMutation();
          toast.success(t('hub.toast.restored', 'Contacto restaurado'));
        } catch (err) {
          toast.error(t('hub.toast.undoError', 'No se pudo deshacer. Restaura el contacto manualmente.'));
        }
      });
    } catch (err) {
      if (isMountedRef.current) toast.error(t('hub.toast.archiveError', 'No se pudo archivar el contacto'));
    }
  };

  const isEmptyNoFilters = !listLoading && contacts.length === 0 && !hasActiveFilters;
  const isEmptyByFilters = !listLoading && contacts.length === 0 && hasActiveFilters;

  return (
    <div className="w-full dashboard-container hub-mobile-optimize p-6 hb-wrap">
      <style>{CSS}</style>

      <div className="hb-header-row">
        <div>
          <h1 className="hb-title">{t('hub.title', 'Hub de Contactos')}</h1>
          <p className="hb-subtitle">{t('hub.subtitle', 'Gestiona y da seguimiento a tus contactos comerciales.')}</p>
        </div>
        <div className="hb-header-actions">
          <label className="hb-btn hb-btn-secondary" style={{ cursor: scanning ? 'not-allowed' : 'pointer', opacity: scanning ? 0.6 : 1 }}>
            <Icon name="camera" className="w-4 h-4" />
            {scanning ? t('hub.scanning', 'Escaneando…') : t('hub.scanCard', 'Escanear tarjeta')}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleScanCard}
              className="hidden"
              disabled={scanning}
              aria-label={t('hub.scanCardAria', 'Escanear tarjeta de presentación')}
              style={{ display: 'none' }}
            />
          </label>
          <button
            onClick={() => setShowAddForm(true)}
            className="hb-btn hb-btn-primary"
            aria-label={t('hub.addContact', 'Agregar contacto')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('hub.addContact', 'Agregar contacto')}
          </button>
        </div>
      </div>

      <div className="hb-metrics" role="group" aria-label={t('hub.metricsAria', 'Métricas de contactos')}>
        <div className="hb-metric-card">
          <div className="hb-metric-value">{metricsLoading ? <Skeleton width={28} height={20} /> : totalAnim}</div>
          <div className="hb-metric-label">{t('hub.metrics.total', 'Total de contactos')}</div>
        </div>
        <div className="hb-metric-card">
          <div className="hb-metric-value">{metricsLoading ? <Skeleton width={28} height={20} /> : nuevosAnim}</div>
          <div className="hb-metric-label">{t('hub.metrics.new', 'Nuevos')}</div>
        </div>
        <div className="hb-metric-card">
          <div className="hb-metric-value">{metricsLoading ? <Skeleton width={28} height={20} /> : pendientesAnim}</div>
          <div className="hb-metric-label">{t('hub.metrics.pending', 'Pendientes de contacto')}</div>
        </div>
        <div className="hb-metric-card">
          <div className="hb-metric-value">{metricsLoading ? <Skeleton width={28} height={20} /> : accionesPendientesAnim}</div>
          <div className="hb-metric-label">{t('hub.metrics.pendingActions', 'Acciones pendientes')}</div>
        </div>
      </div>
      {metrics.capped && (
        <p className="hb-metrics-caption">
          {t('hub.metrics.cappedNote', 'Nuevos, pendientes y acciones pendientes se calculan sobre los primeros 200 contactos cargados. El total es exacto.')}
        </p>
      )}

      <div className="hb-filters">
        <div style={{ ...FILTER_WRAP_STYLE, ...(focusedFilter === 'search' ? FILTER_WRAP_FOCUS_STYLE : null) }}>
          <span style={FILTER_ICON_STYLE}><FilterSearchGlyph /></span>
          <input
            type="text"
            style={FILTER_SEARCH_INPUT_STYLE}
            placeholder={t('hub.filters.searchPlaceholder', 'Buscar por nombre, email, teléfono o empresa')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setFocusedFilter('search')}
            onBlur={() => setFocusedFilter(null)}
            aria-label={t('hub.filters.searchAria', 'Buscar contactos')}
          />
        </div>
        <div style={FILTER_SELECT_WRAP_STYLE}>
          <select
            style={{ ...FILTER_SELECT_STYLE, ...(focusedFilter === 'status' ? FILTER_SELECT_FOCUS_STYLE : null) }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            onFocus={() => setFocusedFilter('status')}
            onBlur={() => setFocusedFilter(null)}
            aria-label={t('hub.filters.statusAria', 'Filtrar por estado')}
          >
            <option value="">{t('hub.filters.allStatuses', 'Todos los estados')}</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{statusLabel(o.value, t)}</option>
            ))}
          </select>
          <span style={FILTER_CHEVRON_STYLE}><FilterChevronGlyph /></span>
        </div>
        <div style={FILTER_SELECT_WRAP_STYLE}>
          <select
            style={{ ...FILTER_SELECT_STYLE, ...(focusedFilter === 'source' ? FILTER_SELECT_FOCUS_STYLE : null) }}
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            onFocus={() => setFocusedFilter('source')}
            onBlur={() => setFocusedFilter(null)}
            aria-label={t('hub.filters.sourceAria', 'Filtrar por fuente')}
          >
            <option value="">{t('hub.filters.allSources', 'Todas las fuentes')}</option>
            {Object.keys(SOURCE_LABELS).map((value) => (
              <option key={value} value={value}>{sourceLabel(value, t)}</option>
            ))}
          </select>
          <span style={FILTER_CHEVRON_STYLE}><FilterChevronGlyph /></span>
        </div>
        <div style={FILTER_SELECT_WRAP_STYLE}>
          <select
            style={{ ...FILTER_SELECT_STYLE, ...(focusedFilter === 'interest' ? FILTER_SELECT_FOCUS_STYLE : null) }}
            value={interestFilter}
            onChange={(e) => setInterestFilter(e.target.value)}
            onFocus={() => setFocusedFilter('interest')}
            onBlur={() => setFocusedFilter(null)}
            aria-label={t('hub.filters.interestAria', 'Filtrar por nivel de interés')}
          >
            <option value="">{t('hub.filters.allLevels', 'Todos los niveles')}</option>
            {INTEREST_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{interestLabel(o.value, t)}</option>
            ))}
          </select>
          <span style={FILTER_CHEVRON_STYLE}><FilterChevronGlyph /></span>
        </div>
        <label className="hb-toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          {t('hub.filters.showArchived', 'Mostrar archivados')}
        </label>
        {hasActiveFilters && (
          <button type="button" className="hb-btn-text" onClick={clearFilters}>
            {t('hub.filters.clear', 'Limpiar filtros')}
          </button>
        )}
      </div>

      {loadError && (
        <div className="hb-error-banner">
          <span>{loadError}</span>
          <button type="button" className="hb-btn hb-btn-secondary" onClick={loadFilteredContacts}>
            {t('hub.retry', 'Reintentar')}
          </button>
        </div>
      )}

      {initialLoading ? (
        <div style={{ marginTop: 16 }}>
          <style>{SKELETON_CSS}</style>
          <div className="hb-table-wrap" style={{ padding: 16 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ display: 'flex', gap: 16, padding: '10px 0', alignItems: 'center' }}>
                <Skeleton width="20%" height={14} />
                <Skeleton width="25%" height={14} />
                <Skeleton width={70} height={20} radius={20} />
                <Skeleton width={60} height={20} radius={20} />
                <Skeleton width="15%" height={14} />
              </div>
            ))}
          </div>
        </div>
      ) : isEmptyNoFilters ? (
        <div className="hb-empty">
          <div className="hb-empty-title">{t('hub.empty.noContactsTitle', 'No tienes contactos aún')}</div>
          <div className="hb-empty-text">{t('hub.empty.noContactsDesc', 'Comienza agregando tus primeros contactos comerciales.')}</div>
          <button onClick={() => setShowAddForm(true)} className="hb-btn hb-btn-primary" style={{ margin: '0 auto' }}>
            {t('hub.empty.addFirst', 'Agregar primer contacto')}
          </button>
        </div>
      ) : isEmptyByFilters ? (
        <div className="hb-empty">
          <div className="hb-empty-title">{t('hub.empty.noMatchesTitle', 'No se encontraron contactos')}</div>
          <div className="hb-empty-text">{t('hub.empty.noMatchesDesc', 'Prueba ajustar la búsqueda o los filtros aplicados.')}</div>
          <button onClick={clearFilters} className="hb-btn hb-btn-secondary" style={{ margin: '0 auto' }}>
            {t('hub.filters.clear', 'Limpiar filtros')}
          </button>
        </div>
      ) : (
        <>
          <div className="hb-table-wrap">
            <table className="hb-table">
              <thead>
                <tr>
                  <th>{t('hub.table.contact', 'Contacto')}</th>
                  <th>{t('hub.table.info', 'Información')}</th>
                  <th>{t('hub.table.status', 'Estado')}</th>
                  <th>{t('hub.table.interest', 'Interés')}</th>
                  <th>{t('hub.table.nextAction', 'Próxima acción')}</th>
                  <th>{t('hub.table.source', 'Fuente')}</th>
                  <th>{t('hub.table.updated', 'Actualización')}</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => {
                  const action = nextActionMeta(contact, dateLocale);
                  const statusStyle = STATUS_STYLE[contact.status] || STATUS_STYLE.new;
                  const interestStyle = contact.interest_level ? INTEREST_STYLE[contact.interest_level] : null;
                  return (
                    <tr
                      key={contact.id}
                      className="hb-table-row"
                      tabIndex={0}
                      role="button"
                      aria-label={t('hub.viewCard', `Ver ficha de ${contact.full_name}`).replace('{name}', contact.full_name)}
                      onClick={() => openDrawer(contact)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrawer(contact); }
                      }}
                    >
                      <td>
                        <div className="hb-cell-name">{contact.full_name}</div>
                        {(contact.company || contact.position) && (
                          <div className="hb-cell-sub">{[contact.company, contact.position].filter(Boolean).join(' · ')}</div>
                        )}
                      </td>
                      <td>
                        {contact.email && <div className="hb-cell-info-row"><Icon name="mail" className="w-3.5 h-3.5" />{contact.email}</div>}
                        {contact.phone && <div className="hb-cell-info-row"><Icon name="phone" className="w-3.5 h-3.5" />{contact.phone}</div>}
                        {!contact.email && !contact.phone && <span className="hb-action-empty">{t('hub.noData', 'Sin datos')}</span>}
                      </td>
                      <td>
                        <span className="hb-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                          {statusLabel(contact.status, t)}
                        </span>
                      </td>
                      <td>
                        {interestStyle ? (
                          <span className="hb-badge" style={{ background: interestStyle.bg, color: interestStyle.color }}>
                            {interestLabel(contact.interest_level, t)}
                          </span>
                        ) : (
                          <span className="hb-action-empty">{t('hub.undefined', 'Sin definir')}</span>
                        )}
                      </td>
                      <td>
                        {action.empty ? (
                          <span className="hb-action-empty">{t('hub.noAction', 'Sin acción')}</span>
                        ) : (
                          <>
                            {action.description && <div className="hb-action-desc">{action.description}</div>}
                            {action.dateLabel && (
                              <span className={`hb-action-date ${action.overdue ? 'hb-action-date-overdue' : action.dueToday ? 'hb-action-date-today' : 'hb-action-date-future'}`}>
                                {action.overdue ? t('hub.overdue', 'Vencida') : action.dueToday ? t('hub.today', 'Hoy') : action.dateLabel}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <span className="hb-source-badge">{sourceLabel(contact.source, t)}</span>
                      </td>
                      <td>
                        <span className="hb-cell-sub">{formatDateShort(contact.updated_at, dateLocale)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="hb-cards-wrap">
            {contacts.map((contact) => {
              const action = nextActionMeta(contact, dateLocale);
              const statusStyle = STATUS_STYLE[contact.status] || STATUS_STYLE.new;
              const interestStyle = contact.interest_level ? INTEREST_STYLE[contact.interest_level] : null;
              const waHref = contact.phone ? `https://wa.me/${normalizeWhatsApp(contact.phone)}` : null;
              return (
                <div
                  key={contact.id}
                  className="hb-mobile-card"
                  role="button"
                  tabIndex={0}
                  aria-label={t('hub.viewCard', `Ver ficha de ${contact.full_name}`).replace('{name}', contact.full_name)}
                  onClick={() => openDrawer(contact)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrawer(contact); }
                  }}
                >
                  <div className="hb-mobile-card-top">
                    <div>
                      <div className="hb-mobile-name">{contact.full_name}</div>
                      {(contact.company || contact.position) && (
                        <div className="hb-mobile-sub">{[contact.company, contact.position].filter(Boolean).join(' · ')}</div>
                      )}
                    </div>
                  </div>
                  <div className="hb-mobile-badges">
                    <span className="hb-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                      {statusLabel(contact.status, t)}
                    </span>
                    {interestStyle && (
                      <span className="hb-badge" style={{ background: interestStyle.bg, color: interestStyle.color }}>
                        {interestLabel(contact.interest_level, t)}
                      </span>
                    )}
                    <span className="hb-source-badge">{sourceLabel(contact.source, t)}</span>
                  </div>
                  <div className="hb-mobile-info">
                    {contact.email && <div className="hb-cell-info-row"><Icon name="mail" className="w-3.5 h-3.5" />{contact.email}</div>}
                    {contact.phone && <div className="hb-cell-info-row"><Icon name="phone" className="w-3.5 h-3.5" />{contact.phone}</div>}
                  </div>
                  {!action.empty && (
                    <div className="hb-mobile-action">
                      {action.description && <div className="hb-action-desc" style={{ maxWidth: 'none', whiteSpace: 'normal' }}>{action.description}</div>}
                      {action.dateLabel && (
                        <span className={`hb-action-date ${action.overdue ? 'hb-action-date-overdue' : action.dueToday ? 'hb-action-date-today' : 'hb-action-date-future'}`}>
                          {action.overdue ? t('hub.overdue', 'Vencida') : action.dueToday ? t('hub.today', 'Hoy') : action.dateLabel}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="hb-mobile-actions">
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} className="hb-mobile-action-btn" onClick={(e) => e.stopPropagation()} aria-label={t('hub.call', 'Llamar')}>
                        <Icon name="phone" className="w-3.5 h-3.5" />{t('hub.call', 'Llamar')}
                      </a>
                    )}
                    {contact.email && (
                      <a href={`mailto:${contact.email}`} className="hb-mobile-action-btn" onClick={(e) => e.stopPropagation()} aria-label={t('hub.sendEmailAria', 'Enviar email')}>
                        <Icon name="mail" className="w-3.5 h-3.5" />{t('hub.email', 'Email')}
                      </a>
                    )}
                    {waHref && (
                      <a href={waHref} target="_blank" rel="noopener noreferrer" className="hb-mobile-action-btn" onClick={(e) => e.stopPropagation()} aria-label={t('hub.openWhatsAppAria', 'Abrir WhatsApp')}>
                        <ChatIcon className="w-3.5 h-3.5" />WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {oldContactsLoaded && oldContacts.length > 0 && (
        <div className="hb-old-section">
          <button
            type="button"
            className="hb-old-toggle"
            onClick={() => setOldContactsOpen(!oldContactsOpen)}
            aria-expanded={oldContactsOpen}
          >
            <div className="hb-old-toggle-left">
              <span className="hb-old-title">{t('hub.legacy.title', 'Contactos antiguos')}</span>
              <span className="hb-old-count">{oldContacts.length}</span>
            </div>
            <ChevronIcon open={oldContactsOpen} />
          </button>
          {oldContactsOpen && (
            <>
              <p className="hb-old-desc">
                {t('hub.legacy.desc', 'Estos registros provienen de una versión anterior del Hub y tienen funciones limitadas.')}
              </p>
              <div className="hb-old-grid">
                {oldContacts.map((hc) => (
                  <div key={hc.id} className="hb-old-card">
                    <div className="hb-old-card-name">{hc.name}</div>
                    {(hc.company || hc.position) && (
                      <div className="hb-old-card-sub">{[hc.company, hc.position].filter(Boolean).join(' · ')}</div>
                    )}
                    {hc.email && <div className="hb-old-card-row"><Icon name="mail" className="w-3.5 h-3.5" />{hc.email}</div>}
                    {hc.phone && <div className="hb-old-card-row"><Icon name="phone" className="w-3.5 h-3.5" />{hc.phone}</div>}
                    <div className="hb-old-card-row">
                      <span className="hb-source-badge">{legacySourceLabel(hc.source, t)}</span>
                    </div>
                    {hc.tags && hc.tags.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {hc.tags.map((tag, i) => (
                          <span key={`${hc.id}-${i}-${tag}`} className="hb-tag" style={{ fontSize: 11, padding: '2px 9px' }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {showAddForm && (
        <div className="hb-modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="hb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hb-modal-inner">
              <div className="hb-modal-header">
                <h2 className="hb-modal-title">{t('hub.addModal.title', 'Agregar Contacto')}</h2>
                <button
                  onClick={() => { setShowAddForm(false); setFormData(EMPTY_CREATE_FORM); }}
                  className="hb-drawer-close"
                  aria-label={t('hub.addModal.closeAria', 'Cerrar formulario')}
                >
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateSubmit}>
                <div className="hb-field">
                  <label className="hb-label" htmlFor="hb-new-name">{t('hub.field.fullName', 'Nombre completo')} *</label>
                  <input
                    id="hb-new-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="hb-field-input"
                    placeholder={t('hub.field.fullNamePlaceholder', 'Juan Pérez')}
                  />
                </div>

                <div className="hb-field-row hb-field">
                  <div>
                    <label className="hb-label" htmlFor="hb-new-company">{t('hub.field.company', 'Empresa')}</label>
                    <input id="hb-new-company" type="text" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="hb-field-input" placeholder="Tech Corp" />
                  </div>
                  <div>
                    <label className="hb-label" htmlFor="hb-new-position">{t('hub.field.position', 'Cargo')}</label>
                    <input id="hb-new-position" type="text" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} className="hb-field-input" placeholder="CEO" />
                  </div>
                </div>

                <div className="hb-field-row hb-field">
                  <div>
                    <label className="hb-label" htmlFor="hb-new-email">{t('hub.field.email', 'Email')}</label>
                    <input id="hb-new-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="hb-field-input" placeholder="contacto@empresa.com" />
                  </div>
                  <div>
                    <label className="hb-label" htmlFor="hb-new-phone">{t('hub.field.phone', 'Teléfono')}</label>
                    <input id="hb-new-phone" type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="hb-field-input" placeholder="+56 9 1234 5678" />
                  </div>
                </div>

                <div className="hb-field">
                  <label className="hb-label" htmlFor="hb-new-tag">{t('hub.field.tags', 'Etiquetas')}</label>
                  <div className="hb-tag-add-row" style={{ marginBottom: 8 }}>
                    <input
                      id="hb-new-tag"
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="hb-field-input"
                      placeholder={t('hub.field.tagsPlaceholder', 'Agregar etiqueta (ej: Cliente, VIP)')}
                    />
                    <button type="button" onClick={handleAddTag} className="hb-tag-add-btn">+</button>
                  </div>
                  <div className="hb-tag-row">
                    {formData.tags.map((tag) => (
                      <span key={tag} className="hb-tag">
                        {tag}
                        <button type="button" onClick={() => handleRemoveTag(tag)} className="hb-tag-remove" aria-label={t('hub.field.removeTagAria', `Quitar etiqueta ${tag}`).replace('{tag}', tag)}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                {hasFeature('campaigns') && campaigns.some((c) => c.status === 'active') && (
                  <div className="hb-field">
                    <label className="hb-label" htmlFor="hb-new-campaign">{t('hub.field.campaign', 'Campaña')}</label>
                    <select
                      id="hb-new-campaign"
                      className="hb-field-select"
                      value={formData.campaign_id}
                      onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
                    >
                      <option value="">{t('hub.field.noCampaign', 'Sin campaña')}</option>
                      {campaigns.filter((c) => c.status !== 'ended').map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="hb-field">
                  <label className="hb-label" htmlFor="hb-new-notes">{t('hub.field.notes', 'Notas')}</label>
                  <textarea
                    id="hb-new-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="hb-field-textarea"
                    placeholder={t('hub.field.notesPlaceholder', 'Información adicional…')}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button type="submit" className="hb-btn-save" disabled={creating}>
                    {creating ? t('hub.saving', 'Guardando…') : t('hub.addModal.save', 'Guardar contacto')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setFormData(EMPTY_CREATE_FORM); }}
                    className="hb-btn-cancel"
                  >
                    {t('hub.cancel', 'Cancelar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {drawerOpen && selectedContact && drawerForm && (
        <div className="hb-drawer-overlay" onClick={closeDrawer}>
          <div className="hb-drawer" ref={drawerRef} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('hub.drawer.aria', `Ficha de ${selectedContact.full_name}`).replace('{name}', selectedContact.full_name)}>
            <div className="hb-drawer-header">
              <div className="hb-drawer-header-top">
                <div>
                  <h2 className="hb-drawer-name">{selectedContact.full_name}</h2>
                  {(selectedContact.company || selectedContact.position) && (
                    <div className="hb-drawer-sub">{[selectedContact.company, selectedContact.position].filter(Boolean).join(' · ')}</div>
                  )}
                  <div className="hb-drawer-meta">
                    <span className="hb-source-badge">{sourceLabel(selectedContact.source, t)}</span>
                    <span>{t('hub.drawer.created', 'Creado')} {formatDateShort(selectedContact.created_at, dateLocale)}</span>
                  </div>
                </div>
                <button onClick={closeDrawer} className="hb-drawer-close" aria-label={t('hub.drawer.closeAria', 'Cerrar ficha de contacto')}>
                  <CloseIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="hb-drawer-body">
              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.drawer.contactSection', 'Contacto')}</div>
                {selectedContact.email && (
                  <div className="hb-contact-row">
                    <Icon name="mail" className="w-4 h-4" style={{ color: P.gray, flexShrink: 0 }} />
                    <span className="hb-contact-row-text">{selectedContact.email}</span>
                    <a href={`mailto:${selectedContact.email}`} className="hb-quick-btn" aria-label={t('hub.sendEmailAria', 'Enviar email')}>
                      <Icon name="mail" className="w-3.5 h-3.5" />{t('hub.send', 'Enviar')}
                    </a>
                  </div>
                )}
                {selectedContact.phone && (
                  <div className="hb-contact-row">
                    <Icon name="phone" className="w-4 h-4" style={{ color: P.gray, flexShrink: 0 }} />
                    <span className="hb-contact-row-text">{selectedContact.phone}</span>
                    <a href={`tel:${selectedContact.phone}`} className="hb-quick-btn" aria-label={t('hub.call', 'Llamar')}>
                      <Icon name="phone" className="w-3.5 h-3.5" />{t('hub.call', 'Llamar')}
                    </a>
                    <a
                      href={`https://wa.me/${normalizeWhatsApp(selectedContact.phone)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hb-quick-btn"
                      aria-label={t('hub.openWhatsAppAria', 'Abrir WhatsApp')}
                    >
                      <ChatIcon className="w-3.5 h-3.5" />WhatsApp
                    </a>
                  </div>
                )}
                {!selectedContact.email && !selectedContact.phone && (
                  <p className="hb-drawer-caption">{t('hub.drawer.noContactInfo', 'Sin datos de contacto disponibles.')}</p>
                )}
              </div>

              <div className="hb-field">
                <label className="hb-label" htmlFor="hb-drawer-name">{t('hub.field.fullName', 'Nombre completo')} *</label>
                <input id="hb-drawer-name" type="text" className="hb-field-input" value={drawerForm.full_name} onChange={(e) => setDrawerForm({ ...drawerForm, full_name: e.target.value })} />
              </div>
              <div className="hb-field-row hb-field">
                <div>
                  <label className="hb-label" htmlFor="hb-drawer-company">{t('hub.field.company', 'Empresa')}</label>
                  <input id="hb-drawer-company" type="text" className="hb-field-input" value={drawerForm.company} onChange={(e) => setDrawerForm({ ...drawerForm, company: e.target.value })} />
                </div>
                <div>
                  <label className="hb-label" htmlFor="hb-drawer-position">{t('hub.field.position', 'Cargo')}</label>
                  <input id="hb-drawer-position" type="text" className="hb-field-input" value={drawerForm.position} onChange={(e) => setDrawerForm({ ...drawerForm, position: e.target.value })} />
                </div>
              </div>
              <div className="hb-field-row hb-field">
                <div>
                  <label className="hb-label" htmlFor="hb-drawer-email">{t('hub.field.email', 'Email')}</label>
                  <input id="hb-drawer-email" type="email" className="hb-field-input" value={drawerForm.email} onChange={(e) => setDrawerForm({ ...drawerForm, email: e.target.value })} />
                </div>
                <div>
                  <label className="hb-label" htmlFor="hb-drawer-phone">{t('hub.field.phone', 'Teléfono')}</label>
                  <input id="hb-drawer-phone" type="tel" className="hb-field-input" value={drawerForm.phone} onChange={(e) => setDrawerForm({ ...drawerForm, phone: e.target.value })} />
                </div>
              </div>

              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.drawer.dealSection', 'Estado comercial')}</div>
                <div className="hb-field-row">
                  <div>
                    <label className="hb-label" htmlFor="hb-drawer-status">{t('hub.table.status', 'Estado')}</label>
                    <select id="hb-drawer-status" className="hb-field-select" value={drawerForm.status} onChange={(e) => setDrawerForm({ ...drawerForm, status: e.target.value })}>
                      {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{statusLabel(o.value, t)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="hb-label" htmlFor="hb-drawer-interest">{t('hub.field.interestLevel', 'Nivel de interés')}</label>
                    <select id="hb-drawer-interest" className="hb-field-select" value={drawerForm.interest_level} onChange={(e) => setDrawerForm({ ...drawerForm, interest_level: e.target.value })}>
                      <option value="">{t('hub.undefined', 'Sin definir')}</option>
                      {INTEREST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{interestLabel(o.value, t)}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.drawer.nextActionSection', 'Próxima acción')}</div>
                <div className="hb-field">
                  <label className="hb-label" htmlFor="hb-drawer-action-desc">{t('hub.field.whatToDo', 'Qué hacer')}</label>
                  <input
                    id="hb-drawer-action-desc"
                    type="text"
                    className="hb-field-input"
                    placeholder={t('hub.field.whatToDoPlaceholder', 'Ej: llamar para cerrar propuesta')}
                    value={drawerForm.next_action_description}
                    onChange={(e) => setDrawerForm({ ...drawerForm, next_action_description: e.target.value })}
                  />
                </div>
                <div className="hb-field">
                  <label className="hb-label" htmlFor="hb-drawer-action-date">{t('hub.field.when', 'Cuándo')}</label>
                  <input
                    id="hb-drawer-action-date"
                    type="date"
                    className="hb-field-input"
                    value={drawerForm.next_action_due_at}
                    onChange={(e) => setDrawerForm({ ...drawerForm, next_action_due_at: e.target.value })}
                  />
                </div>
                <p className="hb-drawer-caption">{t('hub.drawer.actionCaption', 'Planificación manual — no genera recordatorios ni notificaciones automáticas.')}</p>
              </div>

              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.drawer.meetingSection', 'Reunión')}</div>
                <div className="hb-field">
                  <label className="hb-label" htmlFor="hb-drawer-meeting">{t('hub.field.dateTimeOptional', 'Fecha y hora (opcional)')}</label>
                  <input
                    id="hb-drawer-meeting"
                    type="datetime-local"
                    className="hb-field-input"
                    value={drawerForm.meeting_scheduled_at}
                    onChange={(e) => setDrawerForm({ ...drawerForm, meeting_scheduled_at: e.target.value })}
                  />
                </div>
                <p className="hb-drawer-caption">{t('hub.drawer.meetingCaption', 'Solo queda registrada aquí — no crea eventos de calendario.')}</p>
              </div>

              {(drawerForm.status === 'won' || drawerForm.status === 'lost') && (
                <div className="hb-drawer-section">
                  <div className="hb-drawer-section-title">{t('hub.drawer.outcomeSection', 'Resultado')}</div>
                  <div className="hb-field">
                    <label className="hb-label" htmlFor="hb-drawer-outcome">{t('hub.drawer.outcomeSection', 'Resultado')}</label>
                    <select id="hb-drawer-outcome" className="hb-field-select" value={drawerForm.outcome} onChange={(e) => setDrawerForm({ ...drawerForm, outcome: e.target.value })}>
                      <option value="">{t('hub.undefined', 'Sin definir')}</option>
                      {OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{outcomeLabel(o.value, t)}</option>)}
                    </select>
                  </div>
                  <div className="hb-field">
                    <label className="hb-label" htmlFor="hb-drawer-outcome-reason">{t('hub.field.reason', 'Motivo')}</label>
                    <textarea
                      id="hb-drawer-outcome-reason"
                      className="hb-field-textarea"
                      rows={2}
                      placeholder={t('hub.field.reasonPlaceholder', 'Motivo del resultado…')}
                      value={drawerForm.outcome_reason}
                      onChange={(e) => setDrawerForm({ ...drawerForm, outcome_reason: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.field.notes', 'Notas')}</div>
                <textarea
                  className="hb-field-textarea"
                  rows={4}
                  placeholder={t('hub.drawer.notesPlaceholder', 'Información adicional sobre este contacto…')}
                  value={drawerForm.notes}
                  onChange={(e) => setDrawerForm({ ...drawerForm, notes: e.target.value })}
                  aria-label={t('hub.drawer.notesAria', 'Notas del contacto')}
                />
              </div>

              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.field.tags', 'Etiquetas')}</div>
                <div className="hb-tag-add-row" style={{ marginBottom: 8 }}>
                  <input
                    type="text"
                    className="hb-field-input"
                    placeholder={t('hub.field.addTagPlaceholder', 'Agregar etiqueta')}
                    value={newDrawerTag}
                    onChange={(e) => setNewDrawerTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDrawerTag())}
                    aria-label={t('hub.field.addTagPlaceholder', 'Agregar etiqueta')}
                  />
                  <button type="button" onClick={handleAddDrawerTag} className="hb-tag-add-btn">+</button>
                </div>
                <div className="hb-tag-row">
                  {drawerForm.tags.map((tag) => (
                    <span key={tag} className="hb-tag">
                      {tag}
                      <button type="button" onClick={() => handleRemoveDrawerTag(tag)} className="hb-tag-remove" aria-label={t('hub.field.removeTagAria', `Quitar etiqueta ${tag}`).replace('{tag}', tag)}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="hb-drawer-section">
                <div className="hb-drawer-section-title">{t('hub.drawer.infoSection', 'Información')}</div>
                <div className="hb-meta-list">
                  <span>{t('hub.drawer.created', 'Creado')}: {formatDateTimeShort(selectedContact.created_at, dateLocale)}</span>
                  <span>{t('hub.drawer.updated', 'Actualizado')}: {formatDateTimeShort(selectedContact.updated_at, dateLocale)}</span>
                  {selectedContact.is_archived && <span>{t('hub.drawer.archived', 'Archivado')}: {formatDateTimeShort(selectedContact.archived_at, dateLocale)}</span>}
                </div>
              </div>

              <div className="hb-drawer-footer">
                <button type="button" className="hb-btn-save" onClick={handleSaveContact} disabled={saving}>
                  {saving ? t('hub.saving', 'Guardando…') : t('hub.save', 'Guardar')}
                </button>
                <button type="button" className="hb-btn-cancel" onClick={closeDrawer} disabled={saving}>
                  {t('hub.cancel', 'Cancelar')}
                </button>
                <button type="button" className="hb-btn-danger-text" onClick={handleArchive} disabled={saving}>
                  {t('hub.archiveContact', 'Archivar contacto')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubPanel;
